import { Alert, Box, Button, TextField } from '@mui/material'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { authClient, signIn } from '@/auth/client'
import {
  esperaDoErro,
  mensagemDeEntrada,
  mensagemDeFalha,
  mensagemDePasskey,
  temPasskey,
  useContagem,
  useEnvioDeEmail,
} from '@/auth/erros'
import { destinoDeVolta, voltaSearch } from '@/lib/volta'
import NiArrowLeft from '@/ui/icons/nexture/ni-arrow-left'
import NiEmail from '@/ui/icons/nexture/ni-email'
import NiKey from '@/ui/icons/nexture/ni-key'
import { AuthError, AuthScreen, PasswordField } from '@/ui/kit/AuthScreen'

/**
 * Entrada da terapeuta.
 *
 * E-mail e senha vêm primeiro, porque é o que funciona hoje sem passkey
 * cadastrada nem provedor de e-mail. A senha só abre a sessão: o conteúdo
 * clínico continua atrás da chave.
 *
 * A passkey é o caminho previsto, e não é só comodidade: é a extensão PRF dela
 * que desembrulha a chave mestra, então entrar por aqui já deixa o conteúdo
 * clínico a um toque de distância. O código por e-mail é a segunda via, para
 * o computador emprestado e para o aparelho que ficou em casa. Quem entra por
 * código ainda precisa da senha da chave ou do kit para ver conteúdo.
 *
 * Fica fora da camada `_app` para não exigir sessão de quem veio abrir uma.
 */

type Etapa = 'senha' | 'email' | 'codigo'

function Entrar() {
  const navigate = useNavigate()
  const [etapa, setEtapa] = useState<Etapa>('senha')
  const [email, setEmail] = useState('')
  const [codigo, setCodigo] = useState('')
  const [senha, setSenha] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)
  // O bloqueio vem do 429; o reenvio é a nossa espera entre dois códigos.
  const [bloqueio, iniciarBloqueio] = useContagem()
  const [reenvio, iniciarReenvio] = useContagem()
  const envioDeEmail = useEnvioDeEmail()
  const passkeyDisponivel = temPasskey()

  /** Mostra a recusa e, com 429, trava o botão até poder tentar de novo. */
  function recusa(error: Parameters<typeof mensagemDeEntrada>[0], padrao: string): void {
    const segundos = esperaDoErro(error)
    if (segundos !== null) iniciarBloqueio(segundos)
    setErro(mensagemDeEntrada(error, 'terapeuta', padrao))
  }

  const { volta } = Route.useSearch()

  async function entrou(): Promise<void> {
    await navigate({ href: destinoDeVolta(volta, '/app') })
  }

  async function porPasskey(): Promise<void> {
    setErro(null)
    setOcupado(true)
    try {
      const resultado = await signIn.passkey()
      if (resultado?.error) {
        setErro(mensagemDePasskey(resultado.error))
        return
      }
      await entrou()
    } catch {
      setErro(mensagemDeFalha('terapeuta'))
    } finally {
      setOcupado(false)
    }
  }

  async function porSenha(): Promise<void> {
    setErro(null)
    setOcupado(true)
    try {
      const resultado = await signIn.email({ email: email.trim().toLowerCase(), password: senha })
      if (resultado.error) {
        recusa(resultado.error, 'E-mail ou senha errados.')
        return
      }
      await entrou()
    } catch {
      setErro(mensagemDeFalha('terapeuta'))
    } finally {
      setOcupado(false)
    }
  }

  async function pedirCodigo(): Promise<void> {
    setErro(null)
    setOcupado(true)
    try {
      const resultado = await authClient.emailOtp.sendVerificationOtp({
        email: email.trim().toLowerCase(),
        type: 'sign-in',
      })
      if (resultado.error) {
        recusa(resultado.error, 'Não deu para mandar o código.')
        return
      }
      setCodigo('')
      setEtapa('codigo')
      iniciarReenvio(30)
    } catch {
      setErro(mensagemDeFalha('terapeuta'))
    } finally {
      setOcupado(false)
    }
  }

  async function porCodigo(): Promise<void> {
    setErro(null)
    setOcupado(true)
    try {
      const resultado = await signIn.emailOtp({
        email: email.trim().toLowerCase(),
        otp: codigo.trim(),
      })
      if (resultado.error) {
        recusa(resultado.error, 'Código errado ou vencido.')
        return
      }
      await entrou()
    } catch {
      setErro(mensagemDeFalha('terapeuta'))
    } finally {
      setOcupado(false)
    }
  }

  const emailField = (id: string, autoComplete: string) => (
    <TextField
      id={id}
      label="E-mail"
      type="email"
      autoComplete={autoComplete}
      value={email}
      onChange={(event) => setEmail(event.target.value)}
      slotProps={{ htmlInput: { 'data-testid': id } }}
      fullWidth
    />
  )

  const erroBox = erro !== null && <AuthError testId="entrar-erro">{erro}</AuthError>

  if (etapa === 'email' || etapa === 'codigo') {
    return (
      <AuthScreen
        title={etapa === 'email' ? 'Código por e-mail' : 'Digite o código'}
        subtitle={
          etapa === 'email'
            ? 'Para o computador emprestado ou o aparelho que ficou em casa.'
            : 'Mandamos seis dígitos para o seu e-mail. Eles valem por dez minutos.'
        }
        footer={
          <>
            {etapa === 'codigo' && (
              <Button
                variant="text"
                color="grey"
                className="self-start"
                disabled={ocupado || reenvio > 0 || bloqueio > 0}
                onClick={() => void pedirCodigo()}
                data-testid="reenviar-codigo"
              >
                {reenvio > 0 ? `Mandar outro código em ${reenvio} s` : 'Mandar outro código'}
              </Button>
            )}
            <Button
              variant="text"
              color="grey"
              startIcon={<NiArrowLeft size="medium" />}
              className="self-start"
              onClick={() => {
                setErro(null)
                setEtapa(etapa === 'codigo' ? 'email' : 'senha')
              }}
            >
              {etapa === 'codigo' ? 'Usar outro e-mail' : 'Voltar para a senha'}
            </Button>
          </>
        }
      >
        <Box
          component="form"
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault()
            void (etapa === 'email' ? pedirCodigo() : porCodigo())
          }}
        >
          {envioDeEmail === false && (
            <Alert severity="info" className="neutral" data-testid="sem-email">
              O envio de e-mail ainda não está ligado neste servidor, então o código não chega por
              e-mail. Entre pela senha ou pela chave do aparelho.
            </Alert>
          )}
          {etapa === 'email' ? (
            emailField('email', 'email')
          ) : (
            <TextField
              id="codigo"
              label="Código"
              autoComplete="one-time-code"
              value={codigo}
              onChange={(event) => setCodigo(event.target.value)}
              slotProps={{
                htmlInput: {
                  'data-testid': 'codigo',
                  inputMode: 'numeric',
                  className: 'tabular-nums tracking-[0.3em]',
                },
              }}
              fullWidth
              autoFocus
            />
          )}
          {erroBox}
          <Button
            type="submit"
            variant="contained"
            size="large"
            data-testid={etapa === 'email' ? 'pedir-codigo' : 'entrar-codigo'}
            disabled={
              ocupado ||
              bloqueio > 0 ||
              (etapa === 'email' ? email.trim() === '' : codigo.trim() === '')
            }
            loading={ocupado}
          >
            {bloqueio > 0
              ? `Tente de novo em ${bloqueio} s`
              : etapa === 'email'
                ? 'Mandar o código'
                : 'Entrar'}
          </Button>
        </Box>
      </AuthScreen>
    )
  }

  return (
    <AuthScreen
      title="Entrar"
      subtitle="O consultório abre com a sua senha. Os registros continuam atrás da chave."
      footer={
        <>
          <Button
            variant="text"
            color="grey"
            data-testid="entrar-passkey"
            startIcon={<NiKey size="medium" />}
            className="justify-start"
            disabled={ocupado || !passkeyDisponivel}
            onClick={() => void porPasskey()}
          >
            {passkeyDisponivel
              ? 'Entrar com a chave do aparelho'
              : 'Este navegador não usa chave de acesso'}
          </Button>
          <Button
            variant="text"
            color="grey"
            data-testid="entrar-por-email"
            startIcon={<NiEmail size="medium" />}
            className="justify-start"
            onClick={() => {
              setErro(null)
              setEtapa('email')
            }}
          >
            Entrar por código no e-mail
          </Button>
        </>
      }
    >
      <Box
        component="form"
        className="flex flex-col gap-5"
        onSubmit={(event) => {
          event.preventDefault()
          void porSenha()
        }}
      >
        {emailField('email-senha', 'username')}
        <PasswordField
          id="senha"
          testId="senha"
          label="Senha"
          autoComplete="current-password"
          value={senha}
          onChange={(event) => setSenha(event.target.value)}
          fullWidth
        />
        {erroBox}
        <Button
          type="submit"
          variant="contained"
          size="large"
          data-testid="entrar-senha"
          disabled={ocupado || bloqueio > 0 || email.trim() === '' || senha === ''}
          loading={ocupado}
        >
          {bloqueio > 0 ? `Tente de novo em ${bloqueio} s` : 'Entrar'}
        </Button>
      </Box>
    </AuthScreen>
  )
}

export const Route = createFileRoute('/app/entrar')({
  validateSearch: voltaSearch,
  component: Entrar,
})
